import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Search, Plus, User, Mail, Phone, Calendar, MapPin,
  Edit2, Trash2, Eye, FileText, Plane, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contacts() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  const emptyForm = {
    first_name: "", last_name: "", email: "", phone: "",
    alternate_phone: "", address: "", city: "", country: "",
    passport_number: "", passport_expiry: "", date_of_birth: "",
    nationality: "", notes: "", tags: []
  };
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async (search = "") => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await axios.get(`${API}/contacts`, { headers: getAuthHeaders(), params });
      setContacts(res.data);
    } catch (e) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContacts(searchTerm);
  };

  const openAddDialog = () => {
    setFormData(emptyForm);
    setEditMode(false);
    setShowAddDialog(true);
  };

  const openViewContact = async (contact) => {
    try {
      const res = await axios.get(`${API}/contacts/${contact.id}`, { headers: getAuthHeaders() });
      setSelectedContact(res.data);
      setShowDetailDialog(true);
    } catch (e) {
      toast.error("Failed to load contact details");
    }
  };

  const openEditContact = (contact) => {
    setFormData({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      alternate_phone: contact.alternate_phone || "",
      address: contact.address || "",
      city: contact.city || "",
      country: contact.country || "",
      passport_number: contact.passport_number || "",
      passport_expiry: contact.passport_expiry || "",
      date_of_birth: contact.date_of_birth || "",
      nationality: contact.nationality || "",
      notes: contact.notes || "",
      tags: contact.tags || []
    });
    setSelectedContact(contact);
    setEditMode(true);
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error("First name, last name, and email are required");
      return;
    }
    setSaving(true);
    try {
      if (editMode && selectedContact) {
        await axios.patch(`${API}/contacts/${selectedContact.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Contact updated!");
      } else {
        await axios.post(`${API}/contacts`, formData, { headers: getAuthHeaders() });
        toast.success("Contact created!");
      }
      setShowAddDialog(false);
      fetchContacts(searchTerm);
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to save contact";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contactId, fromDetail = false) => {
    if (!window.confirm("Are you sure you want to delete this contact? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`, { headers: getAuthHeaders() });
      toast.success("Contact deleted");
      if (fromDetail) {
        setShowDetailDialog(false);
        navigate("/contacts");
      } else {
        setContacts(prev => prev.filter(c => c.id !== contactId));
      }
    } catch (e) {
      toast.error("Failed to delete contact");
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="contacts-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Contacts</h1>
            <p className="text-slate-600 mt-1">Customer contact records</p>
          </div>
          <Button onClick={openAddDialog} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" /> Add Contact
          </Button>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="outline">Search</Button>
            {searchTerm && (
              <Button type="button" variant="ghost" onClick={() => { setSearchTerm(""); fetchContacts(); }}>
                Clear
              </Button>
            )}
          </div>
        </form>

        {/* Contacts List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
          </div>
        ) : contacts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No contacts found</p>
              <p className="text-sm text-slate-400 mt-1">
                {searchTerm ? "Try a different search term" : "Add your first contact to get started"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map(contact => (
              <Card key={contact.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openViewContact(contact)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{contact.first_name} {contact.last_name}</h3>
                        <p className="text-sm text-slate-500">{contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); openEditContact(contact); }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDelete(contact.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {contact.phone && (
                    <div className="flex items-center gap-2 mt-3 text-sm text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{contact.phone}</span>
                    </div>
                  )}
                  {contact.tags && contact.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {contact.tags.map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Contact Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? "Edit Contact" : "Add New Contact"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData(p => ({ ...p, first_name: e.target.value }))}
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData(p => ({ ...p, last_name: e.target.value }))}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+31 6 12345678"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Alternate Phone</Label>
                  <Input
                    value={formData.alternate_phone}
                    onChange={(e) => setFormData(p => ({ ...p, alternate_phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData(p => ({ ...p, date_of_birth: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                    placeholder="Amsterdam"
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData(p => ({ ...p, country: e.target.value }))}
                    placeholder="Netherlands"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nationality</Label>
                  <Input
                    value={formData.nationality}
                    onChange={(e) => setFormData(p => ({ ...p, nationality: e.target.value }))}
                    placeholder="Dutch"
                  />
                </div>
                <div>
                  <Label>Passport Number</Label>
                  <Input
                    value={formData.passport_number}
                    onChange={(e) => setFormData(p => ({ ...p, passport_number: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Passport Expiry</Label>
                <Input
                  type="date"
                  value={formData.passport_expiry}
                  onChange={(e) => setFormData(p => ({ ...p, passport_expiry: e.target.value }))}
                />
              </div>
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="e.g., VIP, Corporate"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  />
                  <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.tags.map(tag => (
                      <Badge key={tag} className="flex items-center gap-1">
                        {tag}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any additional notes about this customer..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="btn-primary">
                  {saving ? "Saving..." : (editMode ? "Update Contact" : "Create Contact")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Contact Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedContact && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <DialogTitle className="text-xl">{selectedContact.first_name} {selectedContact.last_name}</DialogTitle>
                        <p className="text-sm text-slate-500">{selectedContact.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setShowDetailDialog(false); openEditContact(selectedContact); }}>
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(selectedContact.id, true)}>
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </DialogHeader>
                <div className="mt-4 space-y-6">
                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      {selectedContact.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span>{selectedContact.phone}</span>
                        </div>
                      )}
                      {selectedContact.alternate_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span>{selectedContact.alternate_phone} (alt)</span>
                        </div>
                      )}
                      {selectedContact.date_of_birth && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>DOB: {formatDate(selectedContact.date_of_birth)}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(selectedContact.city || selectedContact.country) && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{[selectedContact.city, selectedContact.country].filter(Boolean).join(", ")}</span>
                        </div>
                      )}
                      {selectedContact.nationality && (
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span>Nationality: {selectedContact.nationality}</span>
                        </div>
                      )}
                      {selectedContact.passport_number && (
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span>Passport: {selectedContact.passport_number} {selectedContact.passport_expiry && `(exp: ${formatDate(selectedContact.passport_expiry)})`}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedContact.tags && selectedContact.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedContact.tags.map(tag => (
                        <Badge key={tag} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  )}

                  {selectedContact.notes && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-600">{selectedContact.notes}</p>
                    </div>
                  )}

                  {/* Booking History */}
                  {selectedContact.booking_history && selectedContact.booking_history.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Plane className="w-4 h-4" /> Booking History
                      </h3>
                      <div className="space-y-2">
                        {selectedContact.booking_history.map(booking => (
                          <div
                            key={booking.id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                            onClick={() => navigate(`/bookings/${booking.id}`)}
                          >
                            <div className="flex items-center gap-3">
                              <Badge className="bg-purple-100 text-purple-700">{booking.pnr || "N/A"}</Badge>
                              <span className="text-sm text-slate-600">{booking.accounting?.invoice_number || "-"}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-semibold">€{booking.selling_price?.toFixed(2)}</span>
                              <Badge variant="outline">{booking.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Request History */}
                  {selectedContact.request_history && selectedContact.request_history.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Request History
                      </h3>
                      <div className="space-y-2">
                        {selectedContact.request_history.map(req => (
                          <div
                            key={req.id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                            onClick={() => navigate(`/requests/${req.id}`)}
                          >
                            <div className="flex items-center gap-3">
                              <Badge className="bg-amber-100 text-amber-700">{req.request_number}</Badge>
                              {req.segments && req.segments[0] && (
                                <span className="text-sm text-slate-600">
                                  {req.segments[0].departure_airport} → {req.segments[req.segments.length - 1]?.arrival_airport}
                                </span>
                              )}
                            </div>
                            <Badge variant="outline">{req.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
